import { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { Maximize2 } from 'lucide-react';
import { READY_NOW } from '@/content';
import { track } from '@/lib/track';
import FullscreenViewer from '@/components/FullscreenViewer';
import RedO from '@/components/RedO';

export default function ReadyNow({ onEnquire }) {
  const imgRef = useRef(null);
  const reduced = useReducedMotion();
  // parallax reveal is desktop-only — mobile always gets the complete, uncropped spread
  const [mob] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [zoom, setZoom] = useState(false);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ['start end', 'center center'] });
  const clip = useTransform(scrollYProgress, [0, 1], ['inset(18% 12% 18% 12% round 28px)', 'inset(0% 0% 0% 0% round 0px)']);
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);
  const still = reduced || mob;
  const IMG = READY_NOW.image;

  return (
    <section id="ready" data-testid="ready-now-section" className="relative scroll-mt-20 overflow-hidden py-20 sm:py-28" aria-labelledby="ready-heading">
      {/* sweeping track arc */}
      <svg className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] opacity-[0.08]" viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="46" fill="none" stroke="#C92F4B" strokeWidth="10" />
      </svg>

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="eyebrow">{READY_NOW.eyebrow}</p>
        <h2 id="ready-heading" className="headline mt-3 text-3xl sm:text-5xl" data-testid="ready-headline">
          <RedO text={READY_NOW.headlineTop} />
          <span className="mt-1 block text-[#646A72]"><RedO text={READY_NOW.headlineBottom} /></span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#404145] sm:text-lg">{READY_NOW.story}</p>

        {/* building reveal — inline position:relative so framer-motion useScroll measures a non-static container (silences scroll-offset warning) */}
        <div ref={imgRef} className="relative mt-12" style={{ position: 'relative' }}>
          <motion.div
            style={still ? {} : { clipPath: clip }}
            className="relative overflow-hidden rounded-3xl shadow-[0_40px_80px_-40px_rgba(28,29,33,0.4)]"
          >
            <motion.div style={still ? {} : { scale }}>
              {/* complete stitched brochure spread — natural aspect, never cropped; tap to open full screen */}
              <div
                role="button"
                tabIndex={0}
                data-testid="ready-image-open"
                aria-label="View the complete ready-building spread full screen"
                onClick={() => setZoom(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') setZoom(true); }}
                className="group relative block w-full cursor-zoom-in bg-[#F4F3EE]"
              >
                <picture>
                  <img
                    src={IMG.full}
                    srcSet={IMG.srcset}
                    sizes="(min-width: 1280px) 1232px, 100vw"
                    width={IMG.width}
                    height={IMG.height}
                    alt={IMG.alt}
                    className="h-auto w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
                <span className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1D21]/85 text-white shadow-lg transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                  <Maximize2 size={17} />
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
        {zoom && <FullscreenViewer src={IMG.full} alt={IMG.alt} onClose={() => setZoom(false)} />}

        {/* three stages */}
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-[#E4E6EB] bg-[#E4E6EB] sm:grid-cols-3">
          {READY_NOW.stages.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`ready-stage-${s.id}`}
              className="group relative bg-white p-7 transition-colors duration-300 hover:bg-[#FAF9F6] sm:p-9"
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ delay: i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-display text-sm font-black text-[#C92F4B]">{s.num}</span>
                <span className="h-px flex-1 bg-[#E4E6EB] transition-colors group-hover:bg-[#C92F4B]/40" />
              </div>
              <h3 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-[#1C1D21]">
                {s.title.split(' ')[0]} <span className="o-red">{s.title.split(' ')[1]?.charAt(0)}</span>{s.title.split(' ')[1]?.slice(1)}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-[#404145]">{s.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-14 flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="headline max-w-md text-xl sm:text-2xl" data-testid="ready-closing">
            {READY_NOW.closing}
          </p>
          <button
            data-testid="ready-schedule-visit-btn"
            onClick={() => { track('book_visit', { placement: 'ready_now' }); onEnquire(); }}
            className="btn-primary"
          >
            Schedule Your Project Visit
          </button>
        </motion.div>
      </div>
    </section>
  );
}
